import React, { useEffect, useRef, useState } from "react";
import Layout from "../Layout/Layout";
import "../css/assessment.css";
import { useNavigate } from "react-router-dom";
import AssessmentBox from "../Components/AssessmentBox";
import HistoryTable from "../Components/HistoryTable";
import { api } from "../actions/api";
import { toast } from "react-toastify";

export default function Assessment() {
  const [activeTab, setActiveTab] = useState("assessment");
  const [assessments, setAssessments] = useState({});
  const [history, setHistory] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    per_page: 10,
    last_page: 1,
  });
  // const [filters, setFilters] = useState({});

  // Delete related states
  const [deleteAssessment, setDeleteAssessment] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // Action menu states
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  //filter
  const [filters, setFilters] = useState({
    sort_by: "assessment_date", // default sort by date
    sort_order: "desc", // default descending order
  });

  const navigate = useNavigate();

  // Fetch assessments
  useEffect(() => {
    const fetchAssessments = async () => {
      setLoadingAssessments(true);
      try {
        const res = await api.get("/user/assessments");
        setAssessments(res.data.data.assessments);
      } catch (err) {
        toast.error("Failed to fetch assessments");
        console.error("Failed to fetch assessments", err);
      } finally {
        setLoadingAssessments(false);
      }
    };

    fetchAssessments();
  }, []);

  // Fetch history with debounce
  useEffect(() => {
    let isMounted = true;
    const timer = setTimeout(() => {
      const fetchHistory = async () => {
        if (isMounted) setLoadingHistory(true);
        try {
          const res = await api.get("/user/assessments/history", {
            params: {
              page: pagination.current_page,
              per_page: pagination.per_page,
              ...filters,
            },
          });

          if (isMounted) {
            const { user_assessments } = res.data.data;
            setHistory(user_assessments || []);
            setPagination(res.data.data.meta);
          }
        } catch (err) {
          if (isMounted) {
            setError("Failed to fetch history data");
            console.error("Failed to fetch history data", err);
          }
        } finally {
          if (isMounted) setLoadingHistory(false);
        }
      };

      if (activeTab === "history") {
        fetchHistory();
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [pagination.current_page, pagination.per_page, filters, activeTab]);

  // Handle page change
  const handlePageChange = (page, perPage = pagination.per_page) => {
    setPagination((prev) => ({
      ...prev,
      current_page: page,
      per_page: perPage,
    }));
  };

  const handleSortChange = (column) => {
    setFilters((prev) => {
      // If clicking on the same column, toggle the order
      if (prev.sort_by === column) {
        return {
          ...prev,
          sort_order: prev.sort_order === "asc" ? "desc" : "asc",
        };
      }
      // If clicking on a different column, set to that column with default desc order
      return {
        ...prev,
        sort_by: column,
        sort_order: "desc",
      };
    });
  };

  // Format history data
  const formattedHistory = history.map((item) => ({
    id: item.id,
    date: new Date(item.assessment_date).toLocaleDateString(),
    type: item.assessment_name,
    typeIcon: item.image,
    assessment_id: item.assessment_id,
    is_completed: item.is_completed,
    score: item.is_completed
      ? `${item.total_obtained_score}/${item.total_score}`
      : "--/--",
    status1: item.is_completed ? undefined : "not-completed",
  }));

  // Unified delete handler
  const handleDelete = async (ids) => {
    setIsDeleting(true);
    try {
      if (ids.length === 1) {
        await api.delete(`/user/assessment/${ids[0]}`);
      } else {
        await api.delete("/user/assessments-delete", {
          data: { ids },
        });
      }

      toast.success(
        ids.length > 1
          ? "Assessments deleted successfully"
          : "Assessment deleted successfully",
      );

      // Force refresh both assessments and history data
      const refreshAssessments = async () => {
        try {
          const res = await api.get("/user/assessments");
          setAssessments(res.data.data.assessments);
        } catch (err) {
          console.error("Failed to refresh assessments", err);
        }
      };

      const refreshHistory = async () => {
        try {
          const res = await api.get("/user/assessments/history", {
            params: {
              page: pagination.current_page,
              per_page: pagination.per_page,
              ...filters,
            },
          });
          setHistory(res.data.data.user_assessments || []);
          setPagination(res.data.data.meta);
        } catch (err) {
          console.error("Failed to refresh history", err);
        }
      };

      // Refresh both data sources
      await Promise.all([refreshAssessments(), refreshHistory()]);

      // Reset selection states
      setSelectedIds([]);
      setDeleteAssessment(null);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          (ids.length > 1
            ? "Failed to delete assessments"
            : "Failed to delete assessment"),
      );
      console.error("Delete error", err);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (isDeleting) return;
    const idsToDelete =
      selectedIds.length > 0 ? selectedIds : [deleteAssessment];
    await handleDelete(idsToDelete);
  };

  // Request to delete single assessment
  const handleRequestDelete = (id) => {
    setDeleteAssessment(id);
    setShowDeleteModal(true);
  };

  // Handle bulk delete click from menu
  const handleBulkDeleteClick = () => {
    if (selectedIds.length > 0) {
      setShowDeleteModal(true);
    } else {
      toast.info("Please select at least one assessment to delete");
    }
    setIsMenuOpen(false);
  };

  const handleBulkSeeClick = () => {
    if (selectedIds.length > 0) {
      navigate("/assessment-view/completed", { state: { id: selectedIds } });
    } else {
      toast.info("Please select at least one assessment to see");
    }
    setIsMenuOpen(false);
  };

  // Handle row selection
  const handleRowSelect = (ids, isSelected) => {
    if (Array.isArray(ids)) {
      // Bulk select (from "select all")
      setSelectedIds((prev) =>
        isSelected
          ? [...new Set([...prev, ...ids])]
          : prev.filter((id) => !ids.includes(id)),
      );
    } else {
      // Single select
      setSelectedIds((prev) =>
        isSelected ? [...prev, ids] : prev.filter((id) => id !== ids),
      );
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Layout>
      <div className="page-content">
        <div className="assessment-content">
          <h1
            className="text-heading-h2 assessment-title-text"
            style={{ color: "#e6ecf1" }}
          >
            Assessment
          </h1>
          <div className="assessment-container">
            <div className="assessment-data">
              {loadingAssessments && activeTab === "assessment" ? (
                <div className="text-center py-5">
                  <div className="loader-container">
                    <div className="loader"></div>
                  </div>
                </div>
              ) : (
                <>
                  <ul className="nav nav-tabs assessment-custom-tabs">
                    <li className="nav-item">
                      <button
                        className={`nav-link text-body-b1 ${
                          activeTab === "assessment" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("assessment")}
                      >
                        Assessment
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link text-body-b1 ${
                          activeTab === "history" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("history")}
                      >
                        History
                      </button>
                    </li>
                  </ul>
                  <div className="tab-content assessment-tab-content">
                    {/* Assessment Tab */}
                    <div
                      id="assessment"
                      className={`tab-pane fade ${
                        activeTab === "assessment" ? "show active" : ""
                      }`}
                    >
                      <div className="d-flex w-100 justify-content-center">
                        <div className="assessment-tab-data">
                          {Object.keys(assessments || {}).length > 0 ? (
                            Object.keys(assessments).map((category) => (
                              <div className="growth-assessment" key={category}>
                                <h1
                                  className="text-heading-h3 mb-0 text-center"
                                  style={{ color: "#ffffff" }}
                                >
                                  The {category} Assessments
                                </h1>
                                <div
                                  className="row"
                                  style={{ justifyContent: "center" }}
                                >

                                  {assessments[category].map((a) => {
                                    const percentage = Number(
                                      (
                                        ((a.current_score ?? 0) /
                                          a.total_score) *
                                        100
                                      ).toFixed(2),
                                    );

                                    return (
                                      <AssessmentBox
                                        key={a.id}
                                        id={a.id}
                                        title={a.name}
                                        status={a.status}
                                        score={a.current_score ?? 0}
                                        total={a.total_score}
                                        percentage={percentage}
                                        percentageColor={
                                          percentage >= 0
                                            ? "#5ce0a0"
                                            : "#eb5757"
                                        }
                                        chartId={`${a.name.toLowerCase()}-chart`}
                                        icon={a.image}
                                        trendIcon={`/icons/assesssment-chart-${
                                          percentage >= 0
                                            ? "increase"
                                            : "decrease"
                                        }.svg`}
                                        score_history={a.score_history}
                                        assessmentId={
                                          a.user_assessment_score_id
                                        }
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                            ))
                          ) : (
                            <p
                              className="text-center text-body-b2"
                              style={{ color: "#ccc" }}
                            >
                              No assessments available.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* History Tab */}
                    <div
                      id="history"
                      className={`tab-pane fade ${
                        activeTab === "history" ? "show active" : ""
                      }`}
                    >
                      {/* Action Menu */}
                      <div className="position-relative d-flex align-items-center justify-content-end mb-3">
                        <button
                          className="btn p-0"
                          onClick={() => setIsMenuOpen(!isMenuOpen)}
                          style={{ background: "transparent", border: "none" }}
                        >
                          <img
                            src="/icons/journal-linear-more.svg"
                            alt="More options"
                            style={{ cursor: "pointer", width: "24px" }}
                          />
                        </button>

                        {isMenuOpen && (
                          <div
                            className="jurnal-library-more-option show"
                            ref={menuRef}
                            style={{ display: "block", width: "240px" }}
                          >
                            <div className="different-options-available">
                              <div
                                className="each-option-available"
                                onClick={handleBulkSeeClick}
                              >
                                <img
                                  alt="See Journal"
                                  src="/icons/journal-image-journal.svg"
                                />
                                <h4
                                  className="text-body-b2 mb-0"
                                  style={{
                                    color: "#ffffff",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  See Assessments ({selectedIds.length})
                                </h4>
                              </div>
                              <div
                                className="each-option-available"
                                onClick={handleBulkDeleteClick}
                              >
                                <img
                                  src="/icons/journal-image-delete.svg"
                                  alt="Delete"
                                />
                                <h4
                                  className="text-body-b2 mb-0"
                                  style={{
                                    color: "#ffffff",
                                    cursor: "pointer",
                                  }}
                                >
                                  Delete ({selectedIds.length})
                                </h4>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="history-table-container">
                        <HistoryTable
                          data={formattedHistory}
                          loading={loadingHistory}
                          pagination={pagination}
                          onPageChange={handlePageChange}
                          onDeleteRequest={handleRequestDelete}
                          selectedIds={selectedIds}
                          onRowSelect={handleRowSelect}
                          onSortChange={handleSortChange}
                          currentSort={filters}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{ backgroundColor: "#132534" }}
            >
              <div className="modal-header position-relative d-flex justify-content-center">
                <button
                  type="button"
                  className="btn-close btn-close-white position-absolute"
                  style={{
                    top: "0px",
                    right: "0px",
                    color: "#e6ecf1",
                  }}
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteAssessment(null);
                  }}
                  disabled={isDeleting}
                ></button>
                <h1
                  className="modal-title"
                  style={{ color: "#e6ecf1", fontSize: "24px" }}
                >
                  {selectedIds.length > 0
                    ? `Delete ${selectedIds.length} ${
                        selectedIds.length > 1 ? "Assessments" : "Assessment"
                      }`
                    : "Delete Assessment"}
                </h1>
              </div>
              <div className="modal-body">
                <p
                  className="text-body-b2 mb-4 text-center"
                  style={{ color: "#e6ecf1", fontSize: "18px" }}
                >
                  {selectedIds.length > 0
                    ? `Are you sure you want to delete ${
                        selectedIds.length
                      } selected ${
                        selectedIds.length > 1 ? "assessments" : "assessment"
                      }?`
                    : "Are you sure you want to delete this assessment?"}
                </p>
                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{
                      width: "182px",
                      background: "#db4545",
                      opacity: isDeleting ? 0.7 : 1,
                    }}
                    onClick={handleConfirmDelete}
                    disabled={
                      isDeleting ||
                      (selectedIds.length === 0 && !deleteAssessment)
                    }
                  >
                    {isDeleting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Deleting...
                      </>
                    ) : (
                      "Yes"
                    )}
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px" }}
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeleteAssessment(null);
                    }}
                    disabled={isDeleting}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
